/**
 * Menu réglages : effacer historique, resync cache, changer d'agent, déconnexion.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Trash2, RefreshCw, UserCog, LogOut, Loader2, CheckCircle2,
  Database, Clock, Calendar,
} from 'lucide-react';
import {
  getAgent, deconnexion, clearSession, sessionInfo,
} from '../services/sessionApi';
import {
  clearCache, syncFromServer, countEtudiants, getLastSync,
} from '../services/etudiantsCache';
import { clearAllCache as clearFichesCache } from '../services/verifApi';

export default function Settings() {
  const navigate = useNavigate();
  const agent = getAgent();
  const [cacheCount, setCacheCount] = useState<number>(0);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [annee, setAnnee] = useState<string>('');
  const [syncing, setSyncing] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const refreshStats = React.useCallback(async () => {
    setCacheCount(await countEtudiants());
    setLastSync(await getLastSync());
  }, []);

  useEffect(() => {
    refreshStats();
    sessionInfo().then(r => setAnnee(r.annee_academique || '')).catch(() => {});
  }, [refreshStats]);

  const flashMsg = (msg: string) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 2500);
  };

  const onResync = async () => {
    setSyncing(true);
    try {
      const r = await syncFromServer();
      flashMsg(`Cache synchronisé : ${r.count} étudiants`);
      refreshStats();
    } catch (e: any) {
      flashMsg('Erreur : ' + (e?.message || 'sync impossible'));
    } finally {
      setSyncing(false);
    }
  };

  const onClearHistory = async () => {
    if (!window.confirm("Effacer l'historique local de vérification et le cache des fiches consultées ?\n\nLes traces côté serveur (backoffice caisse) restent intactes.")) return;
    clearFichesCache();
    flashMsg('Historique local effacé');
  };

  const onClearFullCache = async () => {
    if (!window.confirm("Vider tout le cache local (étudiants + historique) ?\n\nLa prochaine recherche nécessitera une connexion.")) return;
    await clearCache();
    clearFichesCache();
    flashMsg('Cache complet vidé');
    refreshStats();
  };

  const onChangerAgent = async () => {
    if (!window.confirm("Changer d'agent ? Il faudra saisir à nouveau vos identifiants.")) return;
    await deconnexion();
    clearSession();
    navigate('/verif/onboarding', { replace: true });
  };

  const onLogout = async () => {
    if (!window.confirm('Se déconnecter ? Il faudra ressaisir le code au prochain lancement.')) return;
    await deconnexion();
    navigate('/verif/login', { replace: true });
  };

  const fmtDate = (ts: number | null) =>
    ts ? new Date(ts).toLocaleString('fr-FR') : 'jamais';

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-3xl mx-auto p-3 flex items-center gap-2">
          <button
            onClick={() => navigate('/verif', { replace: true })}
            className="p-2 rounded-lg hover:bg-gray-100"
            aria-label="Retour"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500">Réglages</div>
            <div className="font-semibold text-sm truncate">2i Recouvrement</div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-3 space-y-3">
        {agent && (
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Agent connecté</div>
            <div className="font-semibold">{agent.nom_complet}</div>
            {annee && (
              <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                <Calendar size={11} /> Année académique : <b>{annee}</b>
              </div>
            )}
          </div>
        )}

        {flash && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-2.5 text-sm text-emerald-800 flex items-center gap-2">
            <CheckCircle2 size={14} /> {flash}
          </div>
        )}

        {/* Cache */}
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          <div className="p-3 flex items-center gap-2 text-blue-900 font-semibold">
            <Database size={16} /> Cache local
          </div>
          <div className="p-3 text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Étudiants en cache</span>
              <span className="font-mono font-semibold">{cacheCount.toLocaleString('fr-FR')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 flex items-center gap-1"><Clock size={11} /> Dernière synchro</span>
              <span className="text-xs text-gray-700">{fmtDate(lastSync)}</span>
            </div>
          </div>
          <button
            onClick={onResync}
            disabled={syncing}
            className="w-full p-3 text-sm font-semibold text-blue-700 hover:bg-blue-50 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {syncing ? <><Loader2 size={14} className="animate-spin" /> Synchronisation…</>
                     : <><RefreshCw size={14} /> Synchroniser maintenant</>}
          </button>
        </div>

        {/* Historique + purge */}
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          <div className="p-3 flex items-center gap-2 text-orange-800 font-semibold">
            <Trash2 size={16} /> Nettoyage
          </div>
          <button
            onClick={onClearHistory}
            className="w-full p-3 text-sm font-semibold text-orange-700 hover:bg-orange-50 flex items-center justify-center gap-2 text-left"
          >
            <Trash2 size={14} /> Effacer l'historique des vérifications
          </button>
          <button
            onClick={onClearFullCache}
            className="w-full p-3 text-sm font-semibold text-red-700 hover:bg-red-50 flex items-center justify-center gap-2 text-left"
          >
            <Trash2 size={14} /> Vider tout le cache local (étudiants + historique)
          </button>
          <div className="p-3 text-[11px] text-gray-500">
            Les traces des vérifications restent conservées côté caisse dans le backoffice.
            Seul le cache local de votre appareil est effacé.
          </div>
        </div>

        {/* Session */}
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          <div className="p-3 flex items-center gap-2 text-gray-700 font-semibold">
            <UserCog size={16} /> Session
          </div>
          <button
            onClick={onChangerAgent}
            className="w-full p-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
          >
            <UserCog size={14} /> Changer d'agent (nouvel enregistrement)
          </button>
          <button
            onClick={onLogout}
            className="w-full p-3 text-sm font-semibold text-red-700 hover:bg-red-50 flex items-center justify-center gap-2"
          >
            <LogOut size={14} /> Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
}
